/**
 * The services the agency runs for a client, and what switching one off hides
 * (Shawn, 2026-10-05: "if they don't have BrightLocal, they're not an SEO client,
 * turn that off; if we're not running Facebook, shut off Facebook Ads"). Every
 * service is on until someone switches it off in Setup, Portal. The same list
 * lives in utils/clientServices.js on the server (test/services.test.js keeps
 * them in step). No imports and only erasable types, so node --test loads it.
 */
export type ServiceId = "google_ads" | "meta_ads" | "lsa" | "microsoft_ads" | "seo" | "call_center";
export type Service = {
  id: ServiceId;
  name: string;
  /** What the switch does, in one line for the settings list. */
  hides: string;
  /** Client pages left out of the sidebar when the service is off. */
  pages: string[];
  /** Portal sidebar views left out. */
  portalViews: string[];
  /** Ad platforms left out of the portal's channel table. */
  platforms: string[];
};

export const SERVICES: Service[] = [
  { id: "google_ads", name: "Google Ads", hides: "Ads report, Campaigns, Ads AI and Weekly review pages, and the Google Ads row in the portal.", pages: ["/ads", "/ads-campaigns", "/ads-ai", "/ads-review"], portalViews: [], platforms: ["google_ads"] },
  { id: "meta_ads", name: "Facebook and Instagram ads", hides: "The Facebook Ads page, and the Meta Ads row in the portal.", pages: ["/facebook-ads"], portalViews: [], platforms: ["meta"] },
  { id: "lsa", name: "Local Services Ads", hides: "The portal's Calls page and its Local Services Ads row.", pages: [], portalViews: ["calls"], platforms: ["lsa"] },
  { id: "microsoft_ads", name: "Microsoft Ads", hides: "The Microsoft Ads row in the portal.", pages: [], portalViews: [], platforms: ["microsoft_ads"] },
  { id: "seo", name: "SEO, reviews and Google Business Profile", hides: "SEO plan, Reviews & citations (BrightLocal), Keywords, AI search and Website analytics pages, and the SEO report in the portal.", pages: ["/seo-plan", "/reputation", "/keywords", "/ai-search", "/website"], portalViews: ["seo"], platforms: [] },
  { id: "call_center", name: "Call center reports", hides: "Call center reports page, and Call reports in the portal.", pages: ["/call-center"], portalViews: ["reports"], platforms: [] },
];

const offList = (off: readonly string[] | null | undefined) => (Array.isArray(off) ? off : []);

/** Is this client page left out because its service is switched off? */
export function servicesLeaveOut(off: readonly string[] | null | undefined, path: string): boolean {
  const list = offList(off);
  return SERVICES.some((s) => list.includes(s.id) && s.pages.includes(path));
}

/** Portal views to hide for a client. */
export function hiddenPortalViews(off: readonly string[] | null | undefined): string[] {
  const list = offList(off);
  return SERVICES.filter((s) => list.includes(s.id)).flatMap((s) => s.portalViews);
}

/** The name of the switched-off service a page belongs to, for the notice on a direct link. */
export function serviceForPage(off: readonly string[] | null | undefined, path: string): string | null {
  const list = offList(off);
  return SERVICES.find((s) => list.includes(s.id) && s.pages.includes(path))?.name || null;
}
